import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

const HEADER_CONTROL_SIZE = 40
const HEADER_CONTROL_ICON_SIZE = 18
const HEADER_CONTROL_GAP = 8

const Header = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: ${rem(12)} ${rem(16)};
  border-bottom: 1px solid
    ${(props) => props.theme.palette.neutral.surface.lightForeground};
  flex-shrink: 0;
  background-color: ${(props) =>
    props.theme.palette.neutral.surface.normalBackground};
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} ${rem(20)};
    gap: ${rem(16)};
  }
`

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`

const HeaderSpacer = styled.div`
  min-width: 0;
`

const HeaderActions = styled(Group)`
  justify-self: end;
  min-width: 0;
  max-width: 100%;
`

const HeaderControls = styled(Group)`
  min-width: 0;
`

const HeaderControlButton = styled(IconButton)`
  &&& {
    width: ${rem(HEADER_CONTROL_SIZE)};
    height: ${rem(HEADER_CONTROL_SIZE)};
    min-width: ${rem(HEADER_CONTROL_SIZE)};
    min-height: ${rem(HEADER_CONTROL_SIZE)};
    border: 0;
  }

  /* Legacy keeps the light-gray fill; rebrand uses dark primary tokens for opaque contrast. */
  body:not(.${REBRAND_PALETTE_CLASS}) &&& {
    background-color: ${(props) =>
      props.theme.palette.neutral.surface.lightBackground};
  }

  &&& svg {
    width: ${rem(HEADER_CONTROL_ICON_SIZE)};
    height: ${rem(HEADER_CONTROL_ICON_SIZE)};
  }

  body:not(.${REBRAND_PALETTE_CLASS}) &&&:hover,
  body:not(.${REBRAND_PALETTE_CLASS}) &&&:focus-visible,
  body:not(.${REBRAND_PALETTE_CLASS}) &&&:active {
    background-color: ${(props) =>
      props.theme.palette.neutral.surface.lightBackgroundAlt};
  }
`

interface Props {
  children?: ReactNode
  closeOnLeft?: boolean
  onClose: () => void
  onBack?: () => void
  onDelete?: () => void
  headerActions?: ReactNode
}

function TripDrawerHeader({
  children,
  closeOnLeft = false,
  onClose,
  onBack,
  onDelete,
  headerActions,
}: Props) {
  const intl = useIntl()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const controlKind = isRebrand ? 'primary' : 'tertiary'
  const hasContent = !!children
  const showBackButton = !!onBack
  const showHeaderActions = !!headerActions || (!showBackButton && !!onDelete)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleDelete = useCallback(() => {
    closeDropdown()
    onDelete?.()
  }, [closeDropdown, onDelete])

  const closeControl = (
    <HeaderControlButton
      kind={controlKind}
      variant="dark"
      size="medium"
      shape="circle"
      onClick={onClose}
      aria-label={intl.formatMessage({
        id: '4fAcItd9fsE4Za7ZGt6U',
        defaultMessage: 'Close drawer',
        description: 'Accessibility label for button to close the trip drawer',
      })}
    >
      <LineTimesIcon />
    </HeaderControlButton>
  )

  const backLabel = intl.formatMessage({
    id: 'ZUjhekiHLl4vmxd11TN8',
    defaultMessage: 'Back',
    description:
      'Back button label in trip drawer header in the trip planner feature',
  })

  const backControl = showBackButton ? (
    <HeaderControlButton
      kind={controlKind}
      variant="dark"
      size="medium"
      shape="circle"
      onClick={onBack}
      aria-label={backLabel}
    >
      <LineArrowLeftIcon />
    </HeaderControlButton>
  ) : null

  const closeSideControls = (
    <HeaderControls
      direction="horizontal"
      verticalAlign="center"
      gap={HEADER_CONTROL_GAP}
    >
      {closeControl}
      {backControl}
    </HeaderControls>
  )

  let actions: ReactNode = <HeaderSpacer />
  if (showHeaderActions) {
    actions = (
      <HeaderActions direction="horizontal" verticalAlign="center" gap={8}>
        {headerActions}
        {!showBackButton && onDelete && (
          <>
            <IconButton
              className="trip-drawer-header-control"
              kind={controlKind}
              variant="dark"
              size="medium"
              onClick={toggleDropdown}
              ref={dropdownTriggerRef}
              aria-label={intl.formatMessage({
                id: 'u5-AEWKWVERh503zCULl',
                defaultMessage: 'More options',
                description:
                  'Accessibility label for more options menu button in trip drawer',
              })}
            >
              <LineEllipsisVIcon />
            </IconButton>
            <ResponsiveDropdownList
              size="S"
              open={isDropdownOpen}
              onClose={closeDropdown}
              triggerRef={dropdownTriggerRef}
              anchorRef={dropdownTriggerRef}
            >
              <ClickableListItem
                title={intl.formatMessage({
                  id: 'D8VHnehetJNe5buavMUs',
                  defaultMessage: 'Delete',
                  description:
                    'Delete action label in the trip drawer options menu',
                })}
                size="medium"
                startIcon={<LineTrashAltIcon size="S" />}
                onClick={handleDelete}
              />
            </ResponsiveDropdownList>
          </>
        )}
      </HeaderActions>
    )
  }

  let content: ReactNode = <HeaderSpacer />
  if (hasContent) {
    content = <HeaderContent>{children}</HeaderContent>
  }

  return (
    <Header>
      {closeOnLeft && closeSideControls}
      {!closeOnLeft && actions}
      {content}
      {closeOnLeft && actions}
      {!closeOnLeft && closeSideControls}
    </Header>
  )
}

export default TripDrawerHeader
